/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { createApi, fetchBaseQuery, type FetchBaseQueryArgs } from '@reduxjs/toolkit/query/react';
import { setKaryaWorkerIdToken } from '../PrepareHeader';
import type {
  AddAccountRequest,
  VerifyAccountRequest,
  GetTransactionsArgs,
  PaymentSummaryResponse,
  AccountResponse,
  WorkerTransactionResponse,
} from './Types';

const BASE_BACKEND_URL = import.meta.env.VITE_KARYA_BACKEND;
if (!BASE_BACKEND_URL) {
  throw new Error('Missing required env: VITE_KARYA_BACKEND');
}

// Define a service using a base URL and expected endpoints
export let paymentsApi: ReturnType<typeof buildPaymentsApi>;

/**
 * Sets up the Payments API with the given fetch function.
 * @param fetchFn - Optional custom fetch function to use for requests.
 * @returns The configured Payments API.
 */
export function buildPaymentsApi(fetchFn?: FetchBaseQueryArgs['fetchFn']) {
  return createApi({
    reducerPath: 'payments',
    baseQuery: fetchBaseQuery({
      fetchFn,
      baseUrl: BASE_BACKEND_URL,
      prepareHeaders: setKaryaWorkerIdToken,
    }),

    endpoints: (build) => ({
      // Get payment summary for a worker
      getPaymentSummary: build.query<PaymentSummaryResponse, void>({
        query: () => ({
          url: '/api_server_worker/v5/worker/payment_summary',
          method: 'GET',
        }),
      }),

      // Get current active account for a worker
      getCurrentAccount: build.query<AccountResponse, void>({
        query: () => ({
          url: '/api_server_worker/v5/payments/accounts/current',
          method: 'GET',
        }),
      }),

      // Add payment account (UPI or bank account)
      addAccount: build.mutation<AccountResponse, AddAccountRequest>({
        query: (body) => ({
          url: '/api_server_worker/v5/payments/accounts',
          method: 'POST',
          body,
        }),
      }),

      // Verify account after receiving 2rs test payment
      verifyAccount: build.mutation<AccountResponse, { id: string; body: VerifyAccountRequest }>({
        query: ({ id, body }) => ({
          url: `/api_server_worker/v5/payments/accounts/${id}/verify`,
          method: 'PUT',
          body,
        }),
      }),

      // Get all payment transaction records for a worker
      getTransactions: build.query<WorkerTransactionResponse, GetTransactionsArgs | void>({
        query: (args) => {
          const params = new URLSearchParams();
          if (args) {
            if (args.project_id) {
              params.append('project_id', args.project_id);
            }
            if (args.limit !== undefined) {
              params.append('limit', args.limit.toString());
            }
            if (args.offset !== undefined) {
              params.append('offset', args.offset.toString());
            }
          }
          const queryString = params.toString();
          return {
            url: `/api_server_worker/v5/payments/transaction${queryString ? `?${queryString}` : ''}`,
            method: 'GET',
          };
        },
      }),
    }),
  });
}

export function setPaymentsApi(value: ReturnType<typeof buildPaymentsApi>) {
  paymentsApi = value;
}

// Initialize the payments API by default
setPaymentsApi(buildPaymentsApi());
